import React, { Component } from "react";
import { Dimmer, Grid, Loader, Message } from 'semantic-ui-react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from 'ts-debounce';
import { CostCenterDto, UpsertCostCenterDto } from 'ts-types/api.types';
import { FormState } from 'final-form';
import axios, { CancelTokenSource } from 'axios';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import CheckBox from 'components/final-form/CheckBox';
import { required } from 'utils/validatorUtils';
import { noop } from 'utils/functionUtils';
import {
  addCostCenter,
  deleteCostCenter,
  editCostCenter,
  getAllCostCenters,
  searchCostCentersByDescription
} from 'services/costCenterServices';
import { errorUtils } from 'utils/errorUtils';
import DeleteActionButton from 'components/DeleteActionButton';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SaveActionButton from 'components/SaveActionButton';
import CancelActionButton from 'components/CancelActionButton';
import SearchResultComponent from 'components/SearchResultComponent';

const initialCostCenterSearch: Partial<CostCenterSearchRequest> = {};

interface CostCenterSearchRequest {
  description: string;
}

interface CostCenterFieldProps extends FormState {
  values: Partial<CostCenterSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundCostCenters: Array<CostCenterDto>,
  costCentersLoaded: boolean,
  upsertCostCenter?: UpsertCostCenterDto,
  upsertActive: boolean,
  loading: boolean,
  searchValues: Partial<CostCenterSearchRequest>,
  cancelTokenSource: CancelTokenSource,
  errorMessage?: string
}

class CostCenterView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundCostCenters: [],
      costCentersLoaded: false,
      upsertCostCenter: undefined,
      upsertActive: false,
      loading: false,
      searchValues: initialCostCenterSearch,
      cancelTokenSource
    };
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;

    this.mounted = false;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  newCostCenter = () => {

    const defaultValues: CostCenterDto = {
      id: -1,
      description: "",
      baseCenter: false
    };

    this.setState(({foundCostCenters, upsertActive}: State) => ({
      foundCostCenters: [defaultValues, ...foundCostCenters],
      upsertCostCenter: defaultValues,
      upsertActive: !upsertActive
    }))
  };

  editCostCenter = (id: number) => {

    const {foundCostCenters, upsertActive} = this.state;
    const costCenter = foundCostCenters.find((costCenter) => {
      return costCenter.id == id
    });

    this.setState({
      upsertActive: !upsertActive,
      upsertCostCenter: costCenter
    });
  };

  searchAfterSubmit = () => {
    const {searchValues, cancelTokenSource} = this.state;
    const {description} = searchValues;
    const desc = description ? description : "";

    this.setState({costCentersLoaded: false});

    const onFinally = () => {
      this.setState({
        costCentersLoaded: true
      })
    };

    searchCostCentersByDescription(desc, cancelTokenSource)
        .then(response => {
          this.setState({
            foundCostCenters: response,
            upsertActive: false
          });
        })
        .catch(noop)
        .finally(onFinally);


  };

  handleSubmit = (values: Partial<UpsertCostCenterDto>) => {

    const {upsertCostCenter, cancelTokenSource} = this.state;

    if (upsertCostCenter && upsertCostCenter.id != -1) {
      editCostCenter(upsertCostCenter.id, values, cancelTokenSource)
          .then(this.searchAfterSubmit)
          .catch((error) => this.handleError(error.response.data));
    } else {
      addCostCenter(values, cancelTokenSource)
          .then(this.searchAfterSubmit)
          .catch((error) => this.handleError(error.response.data));
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;

    const errorCode = error.errorCode;
    const knownErrors = [errorUtils.deletingReferencedCostCenterNotAllowed];
    if (knownErrors.includes(errorCode)) {
      this.setState({
        errorMessage: getMessage(`cost.center.error.${errorCode}`)
      });
    } else {
      this.setState({
        errorMessage: getMessage('error.general')
      })
    }
  }

  onCancel = () => {
    this.setState(({foundCostCenters, upsertActive}: State) => ({
      foundCostCenters: foundCostCenters.filter(costCenter => costCenter.id != -1),
      upsertCostCenter: undefined,
      upsertActive: !upsertActive
    }));
  };

  deleteCostCenter = (id: number): void => {
    const {foundCostCenters, cancelTokenSource} = this.state;

    deleteCostCenter(id, cancelTokenSource)
        .then(() => {
          this.setState({
            foundCostCenters: foundCostCenters.filter(costCenter => costCenter.id != id)
          })
        })
        .catch((error) => this.handleError(error.response.data));
  };

  handleCostCenterChange = debounce(({values}: CostCenterFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {description} = values;
    const {cancelTokenSource} = this.state;

    const onSuccess = (response: Array<CostCenterDto>) => {
      this.setState({
        foundCostCenters: response
      });
    };

    const onFinally = () => {
      this.setState({
        costCentersLoaded: true
      })
    };

    this.setState({
      costCentersLoaded: false,
      searchValues: values
    });

    if (description) {
      searchCostCentersByDescription(description, cancelTokenSource)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    } else {
      getAllCostCenters(cancelTokenSource)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    }
  }, 300);

  render(): React.ReactNode {
    const {costCentersLoaded} = this.state;

    const costCenterView = (
        <PraxStyledSegment>

          <div>
            {this.renderSearchCostCenterSegment()}
          </div>

          <StyledViewTableRow>
            {!costCentersLoaded ? this.renderLoader() : this.renderCostCentersSegment()}
          </StyledViewTableRow>

        </PraxStyledSegment>
    );
    return (
        <React.Fragment>
          {costCenterView}
        </React.Fragment>
    );
  }

  renderSearchCostCenterSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {upsertActive, errorMessage, foundCostCenters} = this.state;

    const numResults = foundCostCenters.length;

    return (
        <PxGrid>
          {errorMessage && this.renderErrorSegment()}
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="description"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} disabled={upsertActive}
                         onClick={() => this.newCostCenter()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleCostCenterChange}/>
        </PxGrid>
    )
  };

  descriptionCellRenderer = ({rowData, cellData, dataKey}: any) => {
    const {upsertActive, upsertCostCenter} = this.state;
    const costCenterId = rowData.id;

    if (upsertCostCenter && costCenterId == upsertCostCenter.id && upsertActive) {
      const validator = (value: string) => required(value);
      return (
          <React.Fragment>
            <Field
                name={dataKey}
                component={Input}
                validate={validator}
                autoFocus
            />
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          {cellData}
        </React.Fragment>
    );
  };

  baseCenterCellRenderer = ({rowData, cellData, dataKey}: any) => {
    const {getMessage} = this.props;
    const {upsertActive, upsertCostCenter} = this.state;
    const costCenterId = rowData.id;

    if (upsertCostCenter && costCenterId == upsertCostCenter.id && upsertActive) {
      return (
          <React.Fragment>
            <Field
                name={dataKey}
                component={CheckBox}
            />
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          {cellData && getMessage("cost.center.baseCenter")}
        </React.Fragment>
    );
  };

  actionsCellRenderer = ({rowData, cellData, rowIndex}: any) => {
    const {getMessage} = this.props;
    const {upsertActive, upsertCostCenter} = this.state;
    const costCenterId = rowData.id;

    if (upsertCostCenter && costCenterId == upsertCostCenter.id && upsertActive) {
      return (
          <React.Fragment>
            <SaveActionButton hoverMessage={getMessage('button.save')}/>
            <CancelActionButton hoverMessage={getMessage('button.cancel')} onConfirm={() => this.onCancel()}/>
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.editCostCenter(cellData)}
              disabled={upsertActive}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("cost.center.confirmDelete")}
              onConfirm={() => this.deleteCostCenter(cellData)}
              disabled={upsertActive}
          />
        </React.Fragment>
    );
  };

  rowGetter = ({index}: any) => {
    const {foundCostCenters} = this.state;

    Object.assign(foundCostCenters[index], {index: index + 1});

    return foundCostCenters[index];
  };

  foundCostCentersRowRenderer = ({className, columns, index, key, style, rowData}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {upsertCostCenter, upsertActive} = this.state;
    const costCenterId = rowData.id;

    if (upsertCostCenter && costCenterId == upsertCostCenter.id && upsertActive) {

      const styleCopy = style;

      style = {
        position: "relative"
      };

      return (
          <div style={style}
               key={key}
          >
            <FinalForm
                onSubmit={this.handleSubmit}
                initialValues={upsertCostCenter}
                subscription={{pristine: true}}
                render={({handleSubmit}) => (
                    <form onSubmit={handleSubmit}
                          {...a11yProps}
                          className={className}
                          role="row"
                          style={styleCopy}
                    >

                      {columns}
                    </form>
                )}
            />
          </div>
      );
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  renderCostCentersSegment = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {foundCostCenters} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundCostCenters.length}
            rowGetter={this.rowGetter}
            rowRenderer={this.foundCostCentersRowRenderer}
            rowHeight={42}
            scrollToIndex={0}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description",
                cellRenderer: (this.descriptionCellRenderer)
              },
              {
                width: 200,
                flexGrow: 1,
                label: (getMessage("cost.center.baseCenter")),
                dataKey: "baseCenter",
                cellRenderer: (this.baseCenterCellRenderer)
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("cost.center.loading")}</Loader>
        </Dimmer>
    )
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

}

export default withMessages(CostCenterView);
